import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAdRouteParams } from './useAdRouteParams';
import { useAnalyticalDatasetProfileQuery } from '../api/useAnalyticalDatasetProfileQuery';
import { useProcessAnalyticalDatasetMutation } from '../api/useProcessAnalyticalDatasetMutation';
import { getAnalyticalDatasetVersionPath } from '../routes';
import { selectCohorts, selectHasStagedChanges, selectVariables } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type { UseAdProcessModalArgs, UseAdProcessModalResult } from '../types';
import { formatStatusLabel, toAssetStatus } from '../utils/assetStatusLabel';

// Seconds the success panel lingers before auto-closing. Mirrors cohort's
// process modal — the run is already queued, so this is just a confirmation
// the user can read before the modal navigates to the executed version.
const SUCCESS_AUTO_CLOSE_SECONDS = 2;

export const useAdProcessModal = ({ onClose }: UseAdProcessModalArgs): UseAdProcessModalResult => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  const hasStagedChanges = useAnalyticalDatasetDefinitionStore(selectHasStagedChanges);
  const cohorts = useAnalyticalDatasetDefinitionStore(selectCohorts);
  const variables = useAnalyticalDatasetDefinitionStore(selectVariables);

  // The "from {X} to Processing" copy reflects the real current status (Draft /
  // Error / Processed — a dataset can be re-processed from any of them). Capture
  // it at modal open so the optimistic flip to PROCESSING doesn't rewrite it.
  const { data: profileEnvelope } = useAnalyticalDatasetProfileQuery({
    projectId,
    analyticalDatasetProfileId,
  });
  const latestStatus = profileEnvelope?.data?.latestStatus;
  const initialStatusRef = useRef<string | undefined>(undefined);
  if (initialStatusRef.current === undefined && latestStatus) {
    initialStatusRef.current = latestStatus;
  }
  const fromStatusLabel = initialStatusRef.current
    ? formatStatusLabel(toAssetStatus(initialStatusRef.current))
    : formatMessage({ id: 'analyticalDatasetDetail.processModal.draft' });

  const datasetName =
    profileEnvelope?.data?.name ||
    formatMessage({ id: 'analyticalDatasetDetail.processModal.fallbackName' });

  const { mutate, isPending, error, reset } = useProcessAnalyticalDatasetMutation();

  // The modal opens on a confirmation step; the run only fires once the user
  // confirms (mirrors cohort's process confirmation modal). Before then we show
  // the "this will save + queue for processing" copy with confirm/cancel.
  const [hasConfirmed, setHasConfirmed] = useState(false);
  // After the run is queued the modal holds open on a success panel and counts
  // down before navigating to the executed version — so the user gets the same
  // "saved + queued" confirmation cohort shows, not an instant disappear.
  const [succeeded, setSucceeded] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState(SUCCESS_AUTO_CLOSE_SECONDS);
  const executedVersionIdRef = useRef<string | null>(null);

  // Point the URL at whichever version the run executed against so the
  // definition reloads clean and the staged-changes pill clears. `replace: true`
  // so the back button doesn't return to a stale id. Then dismiss the modal.
  const onSuccessComplete = useCallback(() => {
    const executedVersionId = executedVersionIdRef.current;
    if (executedVersionId && projectId && analyticalDatasetProfileId) {
      navigate(
        getAnalyticalDatasetVersionPath(projectId, analyticalDatasetProfileId, executedVersionId),
        { replace: true }
      );
    }
    onClose();
  }, [navigate, projectId, analyticalDatasetProfileId, onClose]);

  // Drive the success countdown: tick once a second, then complete at zero.
  useEffect(() => {
    if (!succeeded) return;
    if (successCountdown <= 0) {
      onSuccessComplete();
      return;
    }
    const timer = setTimeout(() => setSuccessCountdown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [succeeded, successCountdown, onSuccessComplete]);

  const runProcess = useCallback(() => {
    if (!projectId || !analyticalDatasetProfileId || !analyticalDatasetVersionId) return;
    mutate(
      {
        projectId,
        analyticalDatasetProfileId,
        analyticalDatasetVersionId,
        // Only send the definition when dirty — with no staged changes the BE
        // processes the existing version as-is (no new version row).
        ...(hasStagedChanges ? { definition: { cohorts, variables } } : {}),
      },
      {
        onSuccess: (envelope) => {
          // Hold open on the success panel; the countdown handles navigation +
          // close so the user can read the confirmation first.
          executedVersionIdRef.current = envelope.data.analyticalDatasetVersionId;
          setSucceeded(true);
        },
      }
    );
  }, [
    projectId,
    analyticalDatasetProfileId,
    analyticalDatasetVersionId,
    hasStagedChanges,
    cohorts,
    variables,
    mutate,
  ]);

  const onConfirm = useCallback(() => {
    setHasConfirmed(true);
    runProcess();
  }, [runProcess]);

  const onRetry = useCallback(() => {
    reset();
    runProcess();
  }, [reset, runProcess]);

  return {
    title: formatMessage({ id: 'analyticalDatasetDetail.processModal.title' }),
    errorTitle: formatMessage({ id: 'analyticalDatasetDetail.processModal.errorTitle' }),
    errorText: formatMessage({ id: 'analyticalDatasetDetail.processModal.errorText' }),
    retryButtonLabel: formatMessage({ id: 'analyticalDatasetDetail.processModal.retryButton' }),
    isSubmitting: isPending,
    submittingError: error ?? null,
    onRetry,
    confirmTitle: formatMessage({ id: 'analyticalDatasetDetail.processModal.confirmTitle' }),
    confirmMessage1: formatMessage({ id: 'analyticalDatasetDetail.processModal.message1' }),
    fromStatusLabel,
    processingLabel: formatMessage({ id: 'analyticalDatasetDetail.processModal.processing' }),
    processedLabel: formatMessage({ id: 'analyticalDatasetDetail.processModal.ready' }),
    confirmButtonLabel: formatMessage({ id: 'analyticalDatasetDetail.processModal.confirmButton' }),
    hasConfirmed,
    onConfirm,
    onCancel: onClose,
    isSuccess: succeeded,
    successTitle: formatMessage({ id: 'analyticalDatasetDetail.processModal.successTitle' }),
    successText: formatMessage(
      { id: 'analyticalDatasetDetail.processModal.successText' },
      { name: datasetName }
    ),
    successCountdownSeconds: successCountdown,
    onSuccessComplete,
  };
};
